import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

interface CacheEntry {
  url: string;
  blob: Blob;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageCacheService {
  private readonly DB_NAME = 'ImageCacheDB';
  private readonly STORE_NAME = 'images';
  private readonly CACHE_EXPIRY_DAYS = 7;
  private readonly CDN_DOMAIN = 'cdn.istanbul';
  private db: IDBDatabase | null = null;
  private memoryCache: Map<string, string> = new Map();
  private failedUrls: Set<string> = new Set(); // CORS hatası alan URL'leri takip et

  constructor(private http: HttpClient) {
    this.initDB();
  }

  private initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, 1);

      request.onerror = () => {
        console.error('IndexedDB initialization failed');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.cleanExpiredCache();
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const objectStore = db.createObjectStore(this.STORE_NAME, { keyPath: 'url' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  getImageUrl(url: string | null | undefined): Observable<string> {
    if (!url) {
      return of('');
    }

    // Sadece CDN resimlerini cache'le
    if (!url.includes(this.CDN_DOMAIN)) {
      return of(url);
    }

    // CORS hatası aldıysak direkt orijinal URL'i döndür
    if (this.failedUrls.has(url)) {
      return of(url);
    }

    // Memory cache'e bak (en hızlı)
    const memCached = this.memoryCache.get(url);
    if (memCached) {
      return of(memCached);
    }

    // IndexedDB'den kontrol et
    return from(this.getFromIndexedDB(url)).pipe(
      switchMap(cachedBlob => {
        if (cachedBlob) {
          // Cache'de var, blob URL oluştur
          const blobUrl = URL.createObjectURL(cachedBlob);
          this.memoryCache.set(url, blobUrl);
          return of(blobUrl);
        }

        // Cache'de yok, CDN'den indir ve cache'e ekle (arka planda)
        this.downloadAndCache(url);
        
        // Hemen orijinal URL'i döndür (kullanıcı beklemez)
        return of(url);
      }),
      catchError(() => of(url))
    );
  }

  /**
   * Download image and cache it in background
   * CORS hatalarını handle eder
   */
  private downloadAndCache(url: string): void {
    this.loadImageViaImgTag(url).then(result => {
      if (result.blob) {
        // Büyük dosyaları cache'leme (>1MB)
        if (result.blob.size > 1024 * 1024) {
          console.log(`Skipping large image (${Math.round(result.blob.size/1024)}KB): ${url}`);
          return;
        }
        this.saveToIndexedDB(url, result.blob);
      } else if (!result.isCORS) {
        // img tag başarısız ama CORS hatası değilse (örn. network error), XMLHttpRequest dene
        this.downloadViaHttp(url);
      } else {
        // CORS hatası ise failedUrls'e ekle
        this.failedUrls.add(url);
      }
    });
  }

  /**
   * img tag kullanarak resmi yükle (CORS bypass)
   */
  private loadImageViaImgTag(url: string): Promise<{ blob: Blob | null, isCORS: boolean }> {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Canvas'a çiz ve blob'a çevir
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            resolve({ blob: null, isCORS: false });
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          try {
            canvas.toBlob((blob) => {
              if (blob) {
                resolve({ blob, isCORS: false });
              } else {
                resolve({ blob: null, isCORS: false });
              }
            }, 'image/jpeg', 0.95);
          } catch (e) {
            // Tainted canvas - CORS issue
            resolve({ blob: null, isCORS: true });
          }
        } catch (error) {
          // Canvas'a erişim veya çizim hatası
          resolve({ blob: null, isCORS: false });
        }
      };
      
      img.onerror = () => {
        resolve({ blob: null, isCORS: false });
      };
      
      img.src = url;
    });
  }

  /**
   * XMLHttpRequest ile indir (fallback)
   */
  private downloadViaHttp(url: string): void {
    this.http.get(url, { responseType: 'blob' }).subscribe(
      blob => {
        if (blob.size > 1024 * 1024) {
          console.log(`Skipping large image (${Math.round(blob.size/1024)}KB): ${url}`);
          return;
        }
        this.saveToIndexedDB(url, blob);
      },
      error => {
        // CORS hatası aldık, failedUrls'e ekle ki bir daha denemeyelim
        this.failedUrls.add(url);
      }
    );
  }

  private getFromIndexedDB(url: string): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.db) {
        resolve(null);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const objectStore = transaction.objectStore(this.STORE_NAME);
        const request = objectStore.get(url);

        request.onsuccess = () => {
          const entry: CacheEntry | undefined = request.result;
          
          if (!entry) {
            resolve(null);
            return;
          }

          // Expiry kontrolü
          const now = Date.now();
          const expiryTime = this.CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          
          if (now - entry.timestamp > expiryTime) {
            this.deleteFromIndexedDB(url);
            resolve(null);
            return;
          }

          resolve(entry.blob);
        };

        request.onerror = () => {
          console.error('Error reading from IndexedDB:', request.error);
          resolve(null);
        };
      } catch (error) {
        console.error('Error accessing IndexedDB:', error);
        resolve(null);
      }
    });
  }

  private saveToIndexedDB(url: string, blob: Blob): void {
    if (!this.db) {
      return;
    }

    try {
      const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(this.STORE_NAME);
      
      const entry: CacheEntry = {
        url,
        blob,
        timestamp: Date.now()
      };

      const request = objectStore.put(entry);

      request.onsuccess = () => {
        console.log('Image cached successfully:', url);
      };

      request.onerror = () => {
        console.error('Error saving to IndexedDB:', request.error);
      };
    } catch (error) {
      console.error('Error accessing IndexedDB for save:', error);
    }
  }

  private deleteFromIndexedDB(url: string): void {
    if (!this.db) {
      return;
    }

    try {
      const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(this.STORE_NAME);
      objectStore.delete(url);
    } catch (error) {
      console.error('Error deleting from IndexedDB:', error);
    }
  }

  private cleanExpiredCache(): void {
    if (!this.db) {
      return;
    }

    try {
      const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(this.STORE_NAME);
      const request = objectStore.openCursor();
      
      const now = Date.now();
      const expiryTime = this.CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        
        if (cursor) {
          const entry: CacheEntry = cursor.value;
          
          if (now - entry.timestamp > expiryTime) {
            cursor.delete();
          }
          
          cursor.continue();
        }
      };
    } catch (error) {
      console.error('Error cleaning cache:', error);
    }
  }

  clearCache(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const objectStore = transaction.objectStore(this.STORE_NAME);
        const request = objectStore.clear();

        request.onsuccess = () => {
          this.memoryCache.clear();
          this.failedUrls.clear();
          console.log('Image cache cleared');
          resolve();
        };

        request.onerror = () => {
          console.error('Error clearing cache:', request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('Error accessing IndexedDB for clear:', error);
        reject(error);
      }
    });
  }

  preloadImage(url: string): void {
    if (!url || !url.includes(this.CDN_DOMAIN)) return;
    
    if (!this.memoryCache.has(url) && !this.failedUrls.has(url)) {
      this.getImageUrl(url).subscribe();
    }
  }
}